// 管理者ユーザー型
export interface AdminUser {
  id: string;
  username: string;
  displayName: string;
  email: string;
  role: 'admin' | 'operator'; // 管理者 | 運営者
  permissions?: string[];
  createdAt?: string;
  updatedAt?: string;
  lastLoginAt?: string;
  isActive?: boolean;
  // レガシー互換（snake_case）
  created_at?: string;
  updated_at?: string;
  last_login?: string;
  is_active?: boolean;
}

// エンドユーザー型
export interface EndUser {
  id: string;
  phoneNumber: string;
  displayName: string;
  isActive: boolean;
  lastLoginAt?: string;
}

// 顧客情報型（新UI用）
export interface Customer {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  avatar?: string;
  city?: string;
  membershipLevel: 'regular' | 'vip' | 'premium';
  lastSeen: Date;
  totalOrders?: number;
  customTags: string[];
}

// エンドユーザー認証レスポンス型
export interface EndUserAuthResponse {
  success: boolean;
  message?: string;
  user?: EndUser;
  sessionToken?: string;
}

// チャットルーム型
export interface ChatRoom {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'active' | 'waiting' | 'completed' | 'temp_completed' | 'paused' | 'archived';
  status_display?: string; // ステータス表示名
  created_by: string;
  created_by_name: string;
  participant_phone: string;
  participant_email: string;
  participant_name: string;
  auto_close_enabled: boolean;
  auto_close_days: number;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  completed_at?: string; // 完了日時
  reopened_at?: string; // 再開日時
  last_message_at?: string;
  message_count?: number;
  participant_count?: number;
  notes?: string; // チャットルームメモ
  project_no?: string; // 案件番号
  room_number?: number; // ルーム番号（連番）
  custom_tags?: string[]; // お問い合わせタグ
  last_message?: string;
  // リアルタイム接続状態
  is_user_online?: boolean;
  last_user_activity?: string;
  connection_status?: 'online' | 'recent' | 'offline';
  // 同時入室ロック情報（モック同期用）
  locked_by?: string;

  // 招待配信状況（ルーム一覧表示用）
  invitation_delivery?: {
    email?: { status: string; delivered_at?: string | null };
    sms?: { status: string; delivered_at?: string | null };
  };

  // E/Uログイン済み（OTP認証完了 or メッセージ送信済み）
  eu_logged_in?: boolean;
}

// メッセージ型
export interface Message {
  id: string;
  room_id?: string;
  chat_room_id?: string;
  sender_id: string;
  sender_type: 'admin' | 'operator' | 'end_user' | 'system';
  sender_name: string;
  content: string;
  message_type: 'text' | 'file' | 'image' | 'system';
  file_url?: string | null;
  file_name?: string | null;
  file_size?: number | null;
  file_type?: string | null;
  is_read: boolean;
  is_edited?: boolean; // 編集されたかどうか
  edited_at?: string | null; // 編集日時
  created_at: string;
  updated_at: string;
}

// API レスポンス型
export interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

// 認証レスポンス型
export interface AuthResponse {
  success: boolean;
  token: string;
  user: AdminUser;
  message?: string;
}

// チャットルーム一覧レスポンス型
export interface ChatRoomsResponse {
  success: boolean;
  chatRooms: ChatRoom[];
  pagination: {
    page: number;
    limit: number;
    hasMore: boolean;
  };
}

// メッセージ一覧レスポンス型
export interface MessagesResponse {
  success: boolean;
  messages: Message[];
  pagination: {
    page: number;
    limit: number;
    hasMore: boolean;
  };
}

// メッセージ送信リクエスト型
export interface SendMessageRequest {
  content: string;
  sender_type: 'admin' | 'operator' | 'end_user';
  sender_name?: string;
  message_type?: 'text' | 'file' | 'image';
  file_url?: string;
  file_name?: string;
  file_size?: number;
  file_type?: string;
}

// Socket.IO イベント型
export interface SocketEvents {
  // クライアント → サーバー
  authenticate: (token: string) => void;
  'join-room': (roomId: string) => void;
  'leave-room': (roomId: string) => void;
  'send-message': (data: SendMessageRequest & { roomId: string }) => void;
  'typing-start': (roomId: string) => void;
  'typing-stop': (roomId: string) => void;
  'update-status': (status: 'online' | 'away' | 'busy') => void;

  // サーバー → クライアント
  'new-message': (message: Message) => void;
  'message-read': (data: { messageId: string; roomId: string; readBy: string }) => void;
  'user-joined': (data: { userId: string; username: string; roomId: string }) => void;
  'user-left': (data: { userId: string; username: string; roomId: string }) => void;
  'user-typing': (data: { userId: string; username: string; roomId: string; isTyping: boolean }) => void;
  'user-status-changed': (data: { userId: string; status: string }) => void;
  error: (error: string) => void;
}

// フィルター関連の型
export interface FilterOptions {
  status?: ('pending' | 'active' | 'waiting' | 'completed' | 'temp_completed' | 'paused' | 'archived')[];
  assignedOperator?: string;
  tags?: string[];
  priority?: 'high' | 'medium' | 'low';
  dateRange?: {
    start: Date;
    end: Date;
  };
}

// 統計情報型
export interface ChatStats {
  rooms: {
    total: number;
    active: number;
    closed: number;
    archived: number;
  };
  messages: {
    total: number;
    today: number;
    unread: number;
    files: number;
  };
}

// チャットルーム取得レスポンス型
export interface ChatRoomResponse {
  success: boolean;
  data?: ChatRoom;
  chatRoom?: ChatRoom;
  message?: string;
  error?: string;
}

// メッセージテンプレート型
export interface MessageTemplate {
  id: string;
  title: string;
  content: string;
  category: 'general' | 'support' | 'technical' | 'other';
  usage_count: number;
  created_at: string;
}

// テンプレート一覧レスポンス型
export interface TemplatesResponse {
  success: boolean;
  templates: MessageTemplate[];
  message?: string;
  error?: string;
}

// アカウント管理用の型
export interface AccountFormData {
  username: string;
  displayName: string;
  email: string;
  password: string;
  confirmPassword?: string;
  role: 'admin' | 'operator';
  permissions: string[];
  is_active: boolean;
}

export interface AccountResponse {
  success: boolean;
  accounts?: AdminUser[];
  account?: AdminUser;
  message?: string;
  error?: string;
}

// 権限定義
export const PERMISSIONS = {
  ACCOUNT_MANAGEMENT: 'account_management',
  ROOM_MANAGEMENT: 'room_management',
  MESSAGE_MANAGEMENT: 'message_management',
  ANALYTICS_VIEW: 'analytics_view',
  SYSTEM_SETTINGS: 'system_settings',
} as const;

export const ROLE_PERMISSIONS = {
  admin: [
    PERMISSIONS.ACCOUNT_MANAGEMENT,
    PERMISSIONS.ROOM_MANAGEMENT,
    PERMISSIONS.MESSAGE_MANAGEMENT,
    PERMISSIONS.ANALYTICS_VIEW,
    PERMISSIONS.SYSTEM_SETTINGS,
  ],
  operator: [
    PERMISSIONS.ROOM_MANAGEMENT,
    PERMISSIONS.MESSAGE_MANAGEMENT,
    PERMISSIONS.ANALYTICS_VIEW, // 運営者も分析・レポートにアクセス可能
  ],
} as const;

// 固定タグ関連の型定義
export interface FixedTagCategory {
  id: string;
  name: string;
  display_order: number;
  options?: FixedTagOption[];
}

export interface FixedTagOption {
  id: string;
  category_id: string;
  name: string;
  display_order: number;
}

export interface ChatRoomFixedTag {
  id: string;
  chat_room_id: string;
  category_id: string;
  option_id: string;
  category_name: string;
  option_name: string;
}

// ルール分析関連の型定義
export interface AnalyticsOverview {
  messageStats: {
    total: number;
    today: number;
    week: number;
    month: number;
    growth: {
      daily: number;
      weekly: number;
      monthly: number;
    };
  };
  roomStats: {
    total: number;
    active: number;
    closed: number;
    averageMessages: number;
  };
  responseTimeStats: {
    average: number;
    median: number;
    fastest: number;
    slowest: number;
    unit: string;
  };
  satisfactionStats: {
    average: number;
    total_responses: number;
    distribution: Record<string, number>;
  };
}

export interface TimelineData {
  date: string;
  totalMessages: number;
  adminMessages: number;
  userMessages: number;
  newRooms: number;
  closedRooms: number;
}

export interface CategoryAnalysis {
  name: string;
  count: number;
  percentage: number;
  avgResponseTime: number;
}

export interface PerformanceData {
  id: string;
  name: string;
  role: string;
  handledMessages: number;
  averageResponseTime: number;
  satisfactionScore: number;
  activeRooms: number;
  completedRooms: number;
  workingHours: number;
} 